import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';

import { CounterpartyDropDown, CounterpartyType, Invoice } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { useDebounce } from 'hooks/useDebounce';

import { COUNTERPARTY_SEARCH_MIN_CHARACTERS } from 'utils/constants/invoices';
import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';

import { verificationFormItemNoHeight } from 'styles/pages/InvoiceVerificationStyle';

interface ReimbursementAutocompleteProps {
  currentInvoice: Invoice;
  isCreating: boolean;
  onCreateClick: (searchValue: string) => void;
  onClear?: () => void;
  onSelect?: () => void;
}

export const ReimbursementAutocomplete = ({
  currentInvoice,
  isCreating,
  onCreateClick,
  onClear,
  onSelect
}: ReimbursementAutocompleteProps) => {
  const { translate } = useTranslations();
  const { control, setValue } = useFormContext();
  const { getAllDropDownCounterpartiesByCompany } =
    useAdditionalFieldsController();

  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useState<string>('');
  const [options, setOptions] = useState<CounterpartyDropDown[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] =
    useState<CounterpartyDropDown | null>(null);

  const notEnoughCharactersToSearch =
    searchParams.length < COUNTERPARTY_SEARCH_MIN_CHARACTERS;

  const getCounterparties = useCallback(async () => {
    if (notEnoughCharactersToSearch || !currentInvoice.companyId) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    const result = await getAllDropDownCounterpartiesByCompany(
      Number(currentInvoice.companyId),
      CounterpartyType.REIMBURSEMENT,
      searchParams
    );
    setOptions(result);
    setIsLoading(false);
  }, [
    getAllDropDownCounterpartiesByCompany,
    searchParams,
    currentInvoice.companyId,
    notEnoughCharactersToSearch
  ]);

  const debouncedSearch = useDebounce(getCounterparties, 500);

  useEffect(() => {
    if (searchParams.length < COUNTERPARTY_SEARCH_MIN_CHARACTERS) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debouncedSearch();
  }, [searchParams, debouncedSearch]);

  const handleSelect = (option: CounterpartyDropDown) => {
    setValue(
      InvoiceVerificationFieldName.ReimbursementCounterpartyId,
      option.id,
      {
        shouldDirty: true,
        shouldValidate: true
      }
    );
    setValue(
      InvoiceVerificationFieldName.ReimbursementCounterpartyName,
      option.name || '',
      {
        shouldDirty: true
      }
    );
    setValue(
      InvoiceVerificationFieldName.ReimbursementIban,
      option.ibans?.[0]?.iban || '',
      {
        shouldDirty: true
      }
    );
    setSearchParams(option.name || '');
    setSelectedOption(option);
    setOpen(false);
    onSelect?.();
  };

  const handleClear = () => {
    onClear?.();
    setValue(
      InvoiceVerificationFieldName.ReimbursementCounterpartyId,
      undefined,
      {
        shouldDirty: true,
        shouldValidate: true
      }
    );
    setValue(
      InvoiceVerificationFieldName.ReimbursementCounterpartyName,
      undefined,
      {
        shouldDirty: true
      }
    );
    setValue(InvoiceVerificationFieldName.ReimbursementIban, '', {
      shouldDirty: true
    });
    setSearchParams('');
    setSelectedOption(null);
  };

  const showNoResults =
    !notEnoughCharactersToSearch &&
    !isLoading &&
    options.length === 0 &&
    !isCreating;

  return (
    <Box>
      <Controller
        name={InvoiceVerificationFieldName.ReimbursementCounterpartyId}
        control={control}
        rules={{
          required: translate('errors.requiredName')
        }}
        render={({ fieldState: { error } }) => {
          // While the user is searching for a name or creating a new
          // reimbursement the field is intentionally empty, so the required
          // error would be misleading. Only surface it when there is no
          // search term and no creation in progress.
          const showRequiredError = !!error && !searchParams && !isCreating;

          return (
            <Autocomplete
              fullWidth
              open={
                open &&
                !isLoading &&
                (notEnoughCharactersToSearch || options.length > 0)
              }
              value={selectedOption}
              onChange={(_event, newValue) => {
                if (newValue) {
                  handleSelect(newValue);
                } else {
                  handleClear();
                }
              }}
              onInputChange={(_event, value) => {
                setSearchParams(value);
                setIsLoading(true);
              }}
              onOpen={() => setOpen(true)}
              onClose={(_event, reason) => {
                if (reason === 'escape' || reason === 'blur') {
                  setOpen(false);
                }
              }}
              options={options}
              loading={notEnoughCharactersToSearch ? false : isLoading}
              noOptionsText={
                notEnoughCharactersToSearch
                  ? translate('messages.searchRequiredCharacters', {
                      numberOfCharacters: String(
                        COUNTERPARTY_SEARCH_MIN_CHARACTERS
                      )
                    })
                  : translate('labels.noCounterpartiesFound')
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name || ''}
              blurOnSelect
              clearOnBlur={false}
              forcePopupIcon={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('labels.name')}
                  error={showRequiredError}
                  helperText={showRequiredError ? error?.message : ''}
                  sx={verificationFormItemNoHeight}
                  onChange={(event) => {
                    setSearchParams(event.target.value);
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment sx={{ ml: 1 }} position="start">
                          <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                      </InputAdornment>
                    )
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <li key={key} {...rest}>
                    <Typography fontSize="0.875rem" fontWeight={600}>
                      {option.name}
                    </Typography>
                  </li>
                );
              }}
            />
          );
        }}
      />
      {showNoResults && (
        <Box>
          <Typography
            sx={{
              color: '#9CA3AF',
              textAlign: 'left',
              mt: 0.5,
              fontSize: '0.875rem'
            }}
          >
            {translate('labels.noReimbursementsFoundFor', {
              value: searchParams
            })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1.5 }}
            onClick={() => onCreateClick(searchParams)}
          >
            {translate('buttons.createReimbursement')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
